import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AgendaCanalContato,
  AgendaContexto,
  AgendaEstabelecimento,
  AgendaGradeLinha,
  AgendaMedico,
  AgendaOpcao,
  AgendaRetornoDisponivel,
  AgendaVagas,
} from '../../../../interfaces/agenda';
import { AgendaModaisService } from '../../../../services/agenda/agenda-modais.service';
import { AgendaService, mensagemErroAgenda } from '../../../../services/agenda/agenda.service';
import { AgendaIgnorado, elegibilidadeTransferencia } from '../agenda-acoes';
import { dataLocal } from '../agenda-filtro';
import {
  AgendaCascataExame,
  AgendaDestino,
  AgendaDiaVagas,
  AgendaExameAgrupado,
  AgendaItemLote,
  AgendaJanela,
  AgendaMedicoResultante,
  AgendaVagaDestino,
  agruparExames,
  cascataExame,
  corpoTransferir,
  corpoTransferirLote,
  dataCurta,
  diasDaJanela,
  distribuirLote,
  ehOdonto,
  itemQueBloqueou,
  janelasBusca,
  livresDoLote,
  medicoAtual,
  medicoResultante,
  mesclarVagas,
  rotuloDia,
  tipoBuscaVagas,
  vagasPorDia,
} from './agenda-transferir';

const ESPERA_EXAME_MS = 500;
const MIN_BUSCA_EXAME = 3;
const RETORNOS = ['R', 'R13'];

/**
 * Transferência / remarcação. Um agendamento: escolhe o horário, o canal e confirma.
 * Vários: escolhe a agenda de destino num dia e eles ocupam os primeiros livres, em ordem.
 */
@Component({
  selector: 'app-agenda-transferir',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './agenda-transferir.component.html',
  styleUrl: './agenda-transferir.component.scss',
})
export class AgendaTransferirComponent implements OnInit, OnDestroy {
  @Input({ required: true }) linhas!: AgendaGradeLinha[];
  @Input() contexto: AgendaContexto | null = null;

  @Output() fechar = new EventEmitter<void>();
  @Output() salvo = new EventEmitter<string>();

  readonly rotuloDia = rotuloDia;
  readonly dataCurta = dataCurta;
  readonly medicoAtual = medicoAtual;

  origens: AgendaGradeLinha[] = [];
  ignorados: AgendaIgnorado[] = [];
  erroAbertura: string | null = null;
  lote = false;
  ehExame = false;
  odonto = false;

  // Consulta / odonto: Unidade → Especialidade → Médico
  estabelecimentos: AgendaEstabelecimento[] = [];
  especialidades: AgendaOpcao[] = [];
  medicos: AgendaMedico[] = [];
  cdEstabelecimento: number | null = null;
  cdEspecialidade: number | null = null;
  cdMedico: number | null = null;

  // Exame: Exame → Médico → Unidade, a partir dos parceiros do exame
  termoExame = '';
  examesEncontrados: AgendaExameAgrupado[] = [];
  buscandoExame = false;
  erroExame = '';
  exameEscolhido: AgendaExameAgrupado | null = null;
  cascata: AgendaCascataExame = { medicos: [], unidades: [], cdMedico: null, cdEstabelecimento: null, cdAgendas: [] };

  janelas: AgendaJanela[] = [];
  semana = 0;
  /** Resultado da busca já agrupado por dia e agenda. */
  dias: AgendaDiaVagas[] = [];
  buscando = false;
  buscou = false;
  erroBusca = '';
  semanasComFalha = 0;

  destino: AgendaVagaDestino | null = null;
  retornoDestino: AgendaRetornoDisponivel | null = null;
  diaLote: string | null = null;
  agendaLote: AgendaDestino | null = null;

  canais: AgendaCanalContato[] = [];
  cdCanal: number | null = null;
  tentou = false;

  salvando = false;
  erro = '';
  /** Item que travou o lote na última tentativa (o operador pode tirá-lo e reenviar). */
  bloqueado: AgendaGradeLinha | null = null;

  private fecharRegistro: (() => void) | null = null;
  private timerExame: ReturnType<typeof setTimeout> | null = null;
  private seqBusca = 0;
  private seqExame = 0;
  private seqRetorno = 0;
  private seqEsp = 0;
  private seqMed = 0;

  constructor(private api: AgendaService, private modais: AgendaModaisService) {}

  async ngOnInit() {
    this.fecharRegistro = this.modais.abrir();
    const e = elegibilidadeTransferencia(this.linhas ?? []);
    this.origens = e.elegiveis;
    this.ignorados = e.ignorados;
    this.erroAbertura = e.erro;
    if (e.erro) return;
    this.lote = this.origens.length > 1;
    this.ehExame = e.tipo === 'EXAME';
    this.odonto = !this.ehExame && ehOdonto(this.origens[0]);
    this.janelas = janelasBusca(this.hoje());
    const cargas: Promise<unknown>[] = [];
    if (!this.lote) cargas.push(this.carregarCanais());
    if (this.ehExame) {
      // Começa pelo exame de origem, como o original.
      const ds = this.origens[0].dsExame ?? '';
      if (ds.trim().length >= MIN_BUSCA_EXAME) {
        this.termoExame = ds.trim();
        cargas.push(this.buscarExames(this.termoExame));
      }
    } else {
      cargas.push(this.carregarEstabelecimentos());
    }
    await Promise.all(cargas);
  }

  ngOnDestroy() {
    this.fecharRegistro?.();
    this.fecharRegistro = null;
    if (this.timerExame) clearTimeout(this.timerExame);
    this.seqBusca++;
    this.seqExame++;
    this.seqRetorno++;
    this.seqEsp++;
    this.seqMed++;
  }

  @HostListener('document:keydown.escape')
  aoEsc() {
    if (!this.modais.coberto()) this.cancelar();
  }

  cancelar() {
    if (!this.salvando) this.fechar.emit();
  }

  /** Relógio local; sobrescrito nos testes. */
  protected agora(): Date {
    return new Date();
  }

  private hoje(): string {
    return dataLocal(this.agora());
  }

  private horaAgora(): string {
    const d = this.agora();
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  get supervisor(): boolean { return !!this.contexto?.supervisor; }
  get origem(): AgendaGradeLinha | null { return this.origens[0] ?? null; }

  get subtitulo(): string {
    const o = this.origem;
    if (!o) return '';
    return `${o.dsAgenda ?? ''}${o.dsAgenda ? ' · ' : ''}${rotuloDia(o.dtAgenda)}`;
  }

  // --- Cascata de consulta / odonto ---------------------------------------------------------

  private get areaAtuacao(): number | null { return this.odonto ? 1 : null; }

  private async carregarEstabelecimentos() {
    try {
      this.estabelecimentos = await this.api.estabelecimentos({ tipo: 3, areaAtuacao: this.areaAtuacao });
    } catch (e) {
      this.estabelecimentos = [];
      this.erro = mensagemErroAgenda(e, 'Erro ao carregar as unidades.');
    }
    await this.carregarEspecialidades(true);
  }

  /** inicial: pré-seleciona a especialidade da origem, como o original. */
  private async carregarEspecialidades(inicial = false) {
    const seq = ++this.seqEsp;
    try {
      const lista = await this.api.especialidades({ cdEstabelecimento: this.cdEstabelecimento, tipo: 3, areaAtuacao: this.areaAtuacao });
      if (seq !== this.seqEsp) return;
      this.especialidades = lista;
    } catch (e) {
      if (seq !== this.seqEsp) return;
      this.especialidades = [];
      this.erro = mensagemErroAgenda(e, 'Erro ao carregar as especialidades.');
    }
    const o = this.origem!;
    const daOrigem = o.especialidade?.cdEspecialidadeAgend ?? o.especialidade?.cdEspecialidade ?? null;
    const alvo = inicial ? daOrigem : this.cdEspecialidade;
    this.cdEspecialidade = alvo !== null && this.especialidades.some(e => e.codigo === alvo) ? alvo : null;
    await this.carregarMedicos();
  }

  private async carregarMedicos() {
    const seq = ++this.seqMed;
    try {
      const lista = await this.api.medicos({ cdEstabelecimento: this.cdEstabelecimento, cdEspecialidade: this.cdEspecialidade });
      if (seq !== this.seqMed) return;
      this.medicos = lista;
    } catch {
      if (seq !== this.seqMed) return;
      this.medicos = [];
    }
    if (this.cdMedico !== null && !this.medicos.some(m => m.cdMedico === this.cdMedico)) this.cdMedico = null;
  }

  async trocarEstabelecimento(cd: number | null) {
    this.cdEstabelecimento = cd;
    this.limparResultado();
    await this.carregarEspecialidades();
  }

  async trocarEspecialidade(cd: number | null) {
    this.cdEspecialidade = cd;
    this.limparResultado();
    await this.carregarMedicos();
  }

  trocarMedico(cd: number | null) {
    this.cdMedico = cd;
    this.limparResultado();
  }

  // --- Exame --------------------------------------------------------------------------------

  digitarExame(q: string) {
    this.termoExame = q;
    if (this.timerExame) clearTimeout(this.timerExame);
    this.seqExame++;
    if (q.trim().length < MIN_BUSCA_EXAME) {
      this.examesEncontrados = [];
      this.buscandoExame = false;
      return;
    }
    this.timerExame = setTimeout(() => { void this.buscarExames(q.trim()); }, ESPERA_EXAME_MS);
  }

  private async buscarExames(q: string) {
    const seq = ++this.seqExame;
    this.buscandoExame = true;
    this.erroExame = '';
    try {
      const lista = await this.api.exames(q);
      if (seq !== this.seqExame) return;
      this.examesEncontrados = agruparExames(lista);
    } catch {
      if (seq !== this.seqExame) return;
      this.examesEncontrados = [];
      this.erroExame = 'Erro ao buscar exames.';
    } finally {
      if (seq === this.seqExame) this.buscandoExame = false;
    }
  }

  escolherExame(g: AgendaExameAgrupado) {
    this.exameEscolhido = g;
    this.examesEncontrados = [];
    this.termoExame = '';
    this.cascata = cascataExame(g.parceiros, null, null);
    this.limparResultado();
  }

  removerExame() {
    this.exameEscolhido = null;
    this.cascata = { medicos: [], unidades: [], cdMedico: null, cdEstabelecimento: null, cdAgendas: [] };
    this.limparResultado();
  }

  /** Trocar o médico volta a unidade para "Todas" (a lista de unidades depende dele). */
  trocarMedicoExame(cd: string | null) {
    if (!this.exameEscolhido) return;
    this.cascata = cascataExame(this.exameEscolhido.parceiros, cd, null);
    this.limparResultado();
  }

  trocarUnidadeExame(cd: string | null) {
    if (!this.exameEscolhido) return;
    this.cascata = cascataExame(this.exameEscolhido.parceiros, this.cascata.cdMedico, cd);
    this.limparResultado();
  }

  // --- Busca de vagas -----------------------------------------------------------------------

  get podeBuscar(): boolean {
    return !this.buscando && !this.erroAbertura && (!this.ehExame || !!this.exameEscolhido);
  }

  /** Filtro mudou: o resultado na tela deixa de corresponder a ele. */
  private limparResultado() {
    this.seqBusca++;
    this.buscando = false;
    this.buscou = false;
    this.dias = [];
    this.erroBusca = '';
    this.semanasComFalha = 0;
    this.semana = 0;
    this.destino = null;
    this.retornoDestino = null;
    this.diaLote = null;
    this.agendaLote = null;
  }

  /** Três chamadas de 7 dias, uma depois da outra; a semana que falha não derruba as outras. */
  async buscarVagas() {
    if (!this.podeBuscar) return;
    this.limparResultado();
    const seq = this.seqBusca;
    this.buscando = true;
    const o = this.origem!;
    const base = this.ehExame
      ? {
        tipo: tipoBuscaVagas(o),
        cdMedico: this.cascata.cdMedico ? Number(this.cascata.cdMedico) : null,
        cdEstabelecimento: this.cascata.cdEstabelecimento ? Number(this.cascata.cdEstabelecimento) : null,
        cdAgendas: this.cascata.cdAgendas.length ? this.cascata.cdAgendas.join(',') : null,
      }
      : {
        tipo: tipoBuscaVagas(o),
        cdEstabelecimento: this.cdEstabelecimento,
        cdEspecialidade: this.cdEspecialidade,
        cdMedico: this.cdMedico,
      };
    const respostas: (AgendaVagas | null)[] = [];
    let ultimoErro: unknown = null;
    try {
      for (const j of this.janelas) {
        try {
          respostas.push(await this.api.vagas({ ...base, dtInicio: j.dtInicio, dtFim: j.dtFim }));
        } catch (e) {
          ultimoErro = e;
          respostas.push(null);
        }
        if (seq !== this.seqBusca) return;
      }
      this.semanasComFalha = respostas.filter(r => r === null).length;
      if (this.semanasComFalha === respostas.length) {
        this.erroBusca = mensagemErroAgenda(ultimoErro, 'Erro ao buscar vagas.');
        return;
      }
      this.dias = vagasPorDia(mesclarVagas(respostas), this.hoje(), this.horaAgora(), !this.lote);
      this.buscou = true;
      const primeira = this.janelas.findIndex(j => diasDaJanela(this.dias, j).length > 0);
      this.semana = primeira >= 0 ? primeira : 0;
    } finally {
      if (seq === this.seqBusca) this.buscando = false;
    }
  }

  get diasDaSemana(): AgendaDiaVagas[] {
    const j = this.janelas[this.semana];
    return j ? diasDaJanela(this.dias, j) : [];
  }

  vagasNaSemana(i: number): number {
    const j = this.janelas[i];
    if (!j) return 0;
    return diasDaJanela(this.dias, j).reduce((n, d) => n + d.agendas.reduce((m, a) => m + a.vagas.length, 0), 0);
  }

  escolherSemana(i: number) {
    this.semana = i;
    this.diaLote = null;
    this.agendaLote = null;
  }

  // --- Individual ---------------------------------------------------------------------------

  escolherDestino(v: AgendaVagaDestino) {
    this.destino = v;
    this.erro = '';
    void this.verificarRetorno(v);
  }

  get medicoDestino(): AgendaMedicoResultante | null {
    return this.destino && this.origem ? medicoResultante(this.origem, this.destino) : null;
  }

  /** Só avisa (o back decide, com a supervisão passando): retorno que muda de agenda ou de dia. */
  private async verificarRetorno(v: AgendaVagaDestino) {
    const seq = ++this.seqRetorno;
    this.retornoDestino = null;
    const o = this.origem!;
    const classif = o.ieClassifAgenda ?? '';
    const data = (v.dtDisponivel ?? '').slice(0, 10);
    if (this.ehExame || !RETORNOS.includes(classif)) return;
    if (v.cdAgenda === o.cdAgenda && data === o.dtAgenda.slice(0, 10)) return;
    try {
      const r = await this.api.retornoDisponivel(v.cdAgenda, data, classif);
      if (seq === this.seqRetorno) this.retornoDestino = r;
    } catch {
      // Aviso apenas: sem a resposta, a gravação confere o limite.
    }
  }

  get avisoRetorno(): string | null {
    const r = this.retornoDestino;
    if (!r || r.permitido) return null;
    const uso = r.qtLimite !== null ? ` (${r.qtAtual}/${r.qtLimite} usados no dia)` : '';
    const base = r.temRegra === false ? 'A agenda de destino não tem regra de retorno' : `Limite de retorno atingido no destino${uso}`;
    return this.supervisor ? `${base}. Supervisão pode passar.` : `${base}. A transferência será recusada.`;
  }

  // --- Lote ---------------------------------------------------------------------------------

  escolherDiaLote(data: string) {
    this.diaLote = data;
    this.agendaLote = null;
    // A recusa anterior foi contra outro destino: o item que "travou" não vale mais.
    this.bloqueado = null;
    this.erro = '';
  }

  get agendasDoDiaLote(): AgendaDestino[] {
    return this.diasDaSemana.find(d => d.data === this.diaLote)?.agendas ?? [];
  }

  livres(a: AgendaDestino): number {
    return livresDoLote(a).length;
  }

  suficiente(a: AgendaDestino): boolean {
    return this.livres(a) >= this.origens.length;
  }

  mesmaAgenda(a: AgendaDestino): boolean {
    return a.cdAgenda === this.origem?.cdAgenda && this.diaLote === this.origem?.dtAgenda.slice(0, 10);
  }

  escolherAgendaLote(a: AgendaDestino) {
    if (!this.suficiente(a)) return;
    this.agendaLote = a;
    this.bloqueado = null;
    this.erro = '';
  }

  get mapaLote(): AgendaItemLote[] {
    return this.agendaLote ? distribuirLote(this.origens, livresDoLote(this.agendaLote)) : [];
  }

  /** O back preenche a partir do primeiro livre do dia, inclusive os que já passaram hoje. */
  jaPassou(item: AgendaItemLote): boolean {
    const d = item.destino;
    return !!d && (d.dtDisponivel ?? '').slice(0, 10) === this.hoje() && (d.hrInicio ?? '') <= this.horaAgora();
  }

  get algumJaPassou(): boolean {
    return this.mapaLote.some(i => this.jaPassou(i));
  }

  get trocasMedicoLote(): { de: string; para: string; qt: number }[] {
    if (!this.agendaLote) return [];
    const trocas = new Map<string, { de: string; para: string; qt: number }>();
    for (const o of this.origens) {
      const r = medicoResultante(o, this.agendaLote);
      if (!r.trocou || !r.nm) continue;
      const de = medicoAtual(o) || '-';
      const k = `${de}→${r.nm}`;
      const t = trocas.get(k) ?? { de, para: r.nm, qt: 0 };
      t.qt++;
      trocas.set(k, t);
    }
    return [...trocas.values()];
  }

  removerOrigem(o: AgendaGradeLinha) {
    if (this.salvando) return;
    this.origens = this.origens.filter(x => x !== o);
    if (this.bloqueado === o) { this.bloqueado = null; this.erro = ''; }
    if (this.agendaLote && !this.suficiente(this.agendaLote)) this.agendaLote = null;
  }

  // --- Confirmar ----------------------------------------------------------------------------

  get faltando(): string | null {
    if (this.erroAbertura || !this.origens.length) return 'Nada a transferir';
    if (this.lote) return this.agendaLote ? null : 'Escolha a agenda de destino';
    if (!this.destino) return 'Escolha o novo horário';
    if (!this.cdCanal) return 'Informe o canal de contato';
    return null;
  }

  get rotuloConfirmar(): string {
    if (this.salvando) return 'Transferindo...';
    return this.lote ? `Transferir ${this.origens.length} paciente${this.origens.length === 1 ? '' : 's'}` : 'Confirmar transferência';
  }

  async confirmar() {
    if (this.salvando) return;
    this.tentou = true;
    if (this.faltando) return;
    this.salvando = true;
    this.erro = '';
    try {
      if (this.lote) await this.transferirLote();
      else await this.transferirUm();
    } finally {
      this.salvando = false;
    }
  }

  private async transferirUm() {
    const o = this.origem!;
    const d = this.destino!;
    try {
      const r = await this.api.transferir(corpoTransferir(o, d, this.cdCanal!));
      const t = r.dado;
      this.salvo.emit(t?.medicoAlterado && t.nmMedicoDestino
        ? `Agendamento transferido para ${t.nmMedicoDestino}. O atendimento será aberto neste médico.`
        : `${o.paciente?.nmPaciente ?? 'Paciente'} transferido para ${rotuloDia(d.dtDisponivel ?? '')} às ${t?.hrDestino ?? d.hrInicio}.`);
    } catch (e) {
      this.erro = mensagemErroAgenda(e, 'Erro ao transferir agendamento.');
    }
  }

  private async transferirLote() {
    const a = this.agendaLote!;
    this.bloqueado = null;
    try {
      const r = await this.api.transferirLote(corpoTransferirLote(this.origens, a.cdAgenda, this.diaLote!));
      const lote = r.dado;
      let msg = lote?.mensagem || r.mensagem || `${this.origens.length} agendamento(s) transferido(s) com sucesso.`;
      if (lote?.medicoAlterado && lote.nmMedicoDestino) msg += ` ${lote.qtMedicoAlterado} passaram a ser de ${lote.nmMedicoDestino}.`;
      const naoMovidos = (lote?.itens ?? []).filter(i => !i.ok);
      if (naoMovidos.length) {
        msg += ` Não movido${naoMovidos.length > 1 ? 's' : ''}: ${naoMovidos.map(i => `${i.hrOrigem ?? i.nrSequenciaOrigem} (${i.mensagem ?? 'recusado'})`).join('; ')}.`;
      }
      this.salvo.emit(msg);
    } catch (e) {
      this.erro = mensagemErroAgenda(e, 'Erro ao transferir agendamentos.');
      this.bloqueado = itemQueBloqueou(this.erro, this.origens);
    }
  }

  private async carregarCanais() {
    try {
      this.canais = await this.api.canaisContato();
    } catch {
      this.canais = [];
      this.erro = 'Não foi possível carregar os canais de contato.';
    }
  }

  trackOrigem(_: number, o: AgendaGradeLinha) { return o.id; }
  trackVaga(_: number, v: AgendaVagaDestino) { return v.id; }
}
